class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("change", this.onVariantChange);
  }

  async onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.toggleAddButton();

    if (!this.currentVariant) {
      this.toggleAddButton();
      this.setUnavailable();
    } else {
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      await this.renderProductInfo();
      this.dispatchVariantChange();
    }

    this.toggleSaleBadge();
  }

  updateOptions() {
    this.options = Array.from(
      this.querySelectorAll("select"),
      (select) => select.value
    );
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return variant.options.every((option, index) => {
        return this.options[index] === option;
      });
    });
  }

  dispatchVariantChange() {
    document.dispatchEvent(
      new CustomEvent("variant-change", {
        bubbles: true,
        variant: this.currentVariant,
        detail: {
          variant: this.currentVariant,
        },
      })
    );
  }

  updateMedia() {
    if (!this.currentVariant || !this.currentVariant?.featured_media) return;

    const newMedia = document.querySelector(
      `[data-media-id="${this.dataset.section}-${this.currentVariant.featured_media.id}"]`
    );

    if (!newMedia) return;

    const parent = newMedia.parentElement;

    parent.prepend(newMedia);

    window.setTimeout(() => {
      parent.scroll(0, 0);
    });
  }

  updateURL() {
    if (!this.currentVariant) return;
    window.history.replaceState(
      {},
      "",
      `${this.dataset.url}?variant=${this.currentVariant.id}`
    );
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment`
    );

    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');

      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  toggleSaleBadge() {
    const saleBadge = document.querySelector(
      ".product-info .product__badge--sale"
    );
    if (!saleBadge) return;

    const discount =
      this.currentVariant.compare_at_price - this.currentVariant.price;
    const discountPercentage = Math.round(
      (discount / this.currentVariant.compare_at_price) * 100.0
    );

    if (
      discount > 0 &&
      this.currentVariant &&
      this.currentVariant.compare_at_price !== null
    ) {
      saleBadge.textContent = `-${discountPercentage}%`;
      saleBadge.classList.remove("hidden");
    } else {
      saleBadge.classList.add("hidden");
    }
  }

  async renderProductInfo() {
    try {
      const response = await fetch(
        `${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`
      );

      if (!response.ok) throw new Error("Network response was not ok.");

      const dynamicAddButton = document.querySelector(
        ".shopify-payment-button"
      );

      /* NOTE:
        DOM elements cannot be optional, only hidden.
        To ensure that elements are updated correctly the liquid must
        contain the optional elements, then hide or make visible based
        on the response.
      */
      const domToUpdate = [
        [
          `#price-${this.dataset.section}`,
          (destPrice, srcPrice) => {
            if (srcPrice) destPrice.innerHTML = srcPrice.innerHTML;

            destPrice.classList.remove("visibility-hidden");
          },
        ],
        [
          `#product-form-${this.dataset.section} input[name="inventory_quantity"]`,
          (destQtyInput, srcQtyInput) => {
            if (!srcQtyInput) return;

            destQtyInput.value = srcQtyInput.value;
          },
        ],
        [
          "[data-preorder-enabled]",
          (destPreorder, srcPreorder) => {
            if (!srcPreorder) return;

            const isPreorder =
              srcPreorder.getAttribute("data-preorder-enabled") === "true";

            destPreorder.setAttribute("data-preorder-enabled", isPreorder);
            destPreorder.value = srcPreorder.value;

            if (isPreorder) {
              destPreorder.removeAttribute("disabled");
              dynamicAddButton?.classList.add("hidden");
            } else {
              destPreorder.setAttribute("disabled", true);
              dynamicAddButton?.classList.remove("hidden");
            }
          },
        ],
        [
          "[data-inventory-message]",
          (destInv, srcInv) => {
            const message = srcInv.getAttribute(
              "data-inventory-message-content"
            );

            if (message) {
              destInv.innerHTML = srcInv.innerHTML;
              destInv.classList.remove("visibility-hidden");
              destInv.setAttribute("data-inventory-message-content", message);
            } else {
              destInv.innerHTML = "";
              destInv.classList.add("visibility-hidden");
              destInv.setAttribute("data-inventory-message-content", "");
            }
          },
        ],
      ];

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, "text/html");

      domToUpdate.forEach(([selector, cb]) => {
        const destElms = Array.from(document.querySelectorAll(selector));
        const srcElms = Array.from(html.querySelectorAll(selector));

        destElms.forEach((destElm, index) => {
          const srcElm = srcElms[index];

          cb?.(destElm, srcElm);
        });
      });

      if (this.currentVariant) {
        this.toggleAddButton(
          !this.currentVariant.available,
          window.variantStrings.soldOut
        );
      }
    } catch (error) {
      console.log(error);
    }
  }

  toggleAddButton(disable = true, text = "") {
    const addButton = document
      .getElementById(`product-form-${this.dataset.section}`)
      ?.querySelector('[name="add"]');
    const addButtonText = addButton.querySelector(
      "[data-add-to-cart-button-text]"
    );

    // If the variant is available(with stock or preorder) then the button should be enabled
    const availableVariantText =
      document.querySelector(
        `#product-form-${this.dataset.section} [data-preorder-enabled]`
      ).dataset.preorderEnabled === "true"
        ? window.variantStrings.preorder
        : window.variantStrings.addToCart;

    if (!addButton || !addButtonText) return;

    if (disable) {
      this.closest("product-info")
        ? this.closest("product-info")
            .querySelector("[data-inventory-message]")
            .classList.add("visibility-hidden")
        : null;

      addButton.setAttribute("disabled", true);
      if (text) addButtonText.textContent = text;
    } else {
      this.closest("product-info")
        ? this.closest("product-info")
            .querySelector("[data-inventory-message]")
            .classList.remove("visibility-hidden")
        : null;

      addButton.removeAttribute("disabled");
      addButtonText.textContent = availableVariantText;
    }
  }

  setUnavailable() {
    const addButton = document
      .getElementById(`product-form-${this.dataset.section}`)
      ?.querySelector('[name="add"]');

    const addButtonText = addButton.querySelector(
      "[data-add-to-cart-button-text]"
    );
    if (!addButton) return;

    addButtonText.textContent = window.variantStrings.unavailable;

    document
      .getElementById(`price-${this.dataset.section}`)
      ?.classList.add("visibility-hidden");
  }

  getVariantData() {
    this.variantData =
      this.variantData ||
      JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}

if (!customElements.get("variant-selects")) {
  customElements.define("variant-selects", VariantSelects);
}

class VariantRadios extends VariantSelects {
  constructor() {
    super();
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll("fieldset"));
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll("input")).find(
        (radio) => radio.checked
      ).value;
    });
  }
}

if (!customElements.get("variant-radios")) {
  customElements.define("variant-radios", VariantRadios);
}
